/**
 * Subscribe to Socket-API market channels (SSE) for Markets View live sync.
 * Same pattern as Session-UI — same-origin `/api/socket-api` (nginx/vite proxy).
 * Direct browser hits to betting-sse-hub.rimora.org fail (CORS + Helmet CORP).
 */

import { getLabCommonSocketUrl } from '../config/lab'

export type MarketSocketHandler = (data: Record<string, unknown>) => void

/** Channels players + BM console must join for a bookmaker market id. */
export function bookmakerMarketChannels(marketId: string): string[] {
  const mid = String(marketId || '').trim()
  if (!mid) return []
  return [mid, `favouriteMarket_${mid}`, `market_${mid}`]
}

/**
 * Prefer vendor id `1.xxx#bookmaker{n}` (what Bookmaker-API blasts).
 * Route param is often catalog UUID / Mongo `_id` — still join as a twin so
 * either navigate shape receives the tick.
 */
export function bookmakerSubscribeChannels(opts: {
  routeId?: string | null
  vendorMarketId?: string | null
}): string[] {
  const vendor = String(opts.vendorMarketId ?? '').trim()
  const route = String(opts.routeId ?? '').trim()
  const primary = vendor.includes('#bookmaker') ? vendor : route
  const out = bookmakerMarketChannels(primary)
  if (route && route !== primary) out.push(...bookmakerMarketChannels(route))
  if (vendor && vendor !== primary && vendor.includes('#bookmaker')) {
    out.push(...bookmakerMarketChannels(vendor))
  }
  return [...new Set(out)]
}

export function subscribeMarketChannels(
  channels: string[],
  onData: MarketSocketHandler,
): () => void {
  const sseBase = getLabCommonSocketUrl()
  const unique = [...new Set(channels.map((c) => c.trim()).filter(Boolean))]
  if (!sseBase || unique.length === 0) return () => undefined

  const url = `${sseBase}/v1/stream?channels=${encodeURIComponent(unique.join(','))}`
  const es = new EventSource(url)

  const handle = (raw: MessageEvent) => {
    try {
      const parsed = JSON.parse(String(raw.data)) as {
        data?: Record<string, unknown> | Record<string, unknown>[]
      }
      const payload = parsed?.data
      if (Array.isArray(payload)) {
        for (const row of payload) {
          if (row && typeof row === 'object' && !Array.isArray(row)) {
            onData(row as Record<string, unknown>)
          }
        }
        return
      }
      if (payload && typeof payload === 'object') {
        onData(payload as Record<string, unknown>)
      }
    } catch {
      // ignore heartbeats / malformed frames
    }
  }

  es.addEventListener('message', handle as EventListener)
  es.onmessage = handle
  es.onerror = () => {
    // EventSource auto-reconnects
  }

  return () => {
    es.removeEventListener('message', handle as EventListener)
    es.onmessage = null
    es.onerror = null
    es.close()
  }
}

/** Pull back/lay from Lab batb/batl ladders when present. */
export function oddsFromRateRow(row: Record<string, unknown>): {
  back?: number | null
  lay?: number | null
  status?: string
  ratebuttonData?: number
  rateDiff?: number
} {
  const out: {
    back?: number | null
    lay?: number | null
    status?: string
    ratebuttonData?: number
    rateDiff?: number
  } = {}
  if (row.back !== undefined) out.back = row.back == null ? null : Number(row.back)
  if (row.lay !== undefined) out.lay = row.lay == null ? null : Number(row.lay)
  if (Array.isArray(row.batb)) {
    const ladder = Array.isArray(row.batb[0]) ? (row.batb[0] as unknown[]) : (row.batb as unknown[])
    const n = Number(ladder[ladder.length - 1])
    if (Number.isFinite(n)) out.back = n
  }
  if (Array.isArray(row.batl)) {
    const ladder = Array.isArray(row.batl[0]) ? (row.batl[0] as unknown[]) : (row.batl as unknown[])
    const n = Number(ladder[0])
    if (Number.isFinite(n)) out.lay = n
  }
  if (row.status != null) out.status = String(row.status)
  if (row.ratebuttonData != null && Number.isFinite(Number(row.ratebuttonData))) {
    out.ratebuttonData = Number(row.ratebuttonData)
  }
  if (row.rateDiff != null && Number.isFinite(Number(row.rateDiff))) {
    out.rateDiff = Number(row.rateDiff)
  }
  return out
}
